import { Minus, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { useUISettingsStore } from '@/store/useUISettingsStore'

import { ReaderLayoutFields } from '../../shared/reader-layout-fields'
import { Segmented, SettingRow } from '../../shared/setting-controls'
import {
  applyReaderLayout,
  DEFAULT_PDF_SETTINGS,
  DEFAULT_PDF_THEME,
  DEFAULT_PDF_WARMTH,
  PDF_THEME_OPTIONS,
  PdfSettings,
  ScrollMode,
  toReaderLayout,
  ZOOM_PRESETS,
  ZoomLevel,
} from './pdf-settings'

type PdfSettingsDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  settings: PdfSettings
  onSettingsChange: (settings: PdfSettings) => void
  /** 幅/縦に合わせる を数値ズームに換算した倍率 */
  effectiveZoom?: number
}

export const PdfSettingsDialog = ({
  open,
  onOpenChange,
  settings,
  onSettingsChange,
  effectiveZoom,
}: PdfSettingsDialogProps) => {
  const isFooterPinned = useUISettingsStore((state) => state.isReaderFooterPinned)
  const setFooterPinned = useUISettingsStore((state) => state.setReaderFooterPinned)
  const pdfTheme = useUISettingsStore((state) => state.pdfTheme)
  const setPdfTheme = useUISettingsStore((state) => state.setPdfTheme)
  const pdfWarmth = useUISettingsStore((state) => state.pdfWarmth)
  const setPdfWarmth = useUISettingsStore((state) => state.setPdfWarmth)
  const pdfScrollLock = useUISettingsStore((state) => state.pdfScrollLock)
  const setPdfScrollLock = useUISettingsStore((state) => state.setPdfScrollLock)
  const setPageTurnAnimation = useUISettingsStore((state) => state.setPageTurnAnimation)

  const handleZoomChange = (zoom: ZoomLevel) => {
    onSettingsChange({ ...settings, zoom })
  }

  const handleReset = () => {
    onSettingsChange(DEFAULT_PDF_SETTINGS)
    setPdfTheme(DEFAULT_PDF_THEME)
    setPdfWarmth(DEFAULT_PDF_WARMTH)
    setPdfScrollLock(false)
    setPageTurnAnimation(true)
  }

  const currentZoom = typeof settings.zoom === 'number' ? settings.zoom : (effectiveZoom ?? 1)

  const handleZoomIn = () => {
    const newZoom = Math.min(5, Math.round((currentZoom + 0.1) * 100) / 100)
    handleZoomChange(newZoom)
  }

  const handleZoomOut = () => {
    const newZoom = Math.max(0.3, Math.round((currentZoom - 0.1) * 100) / 100)
    handleZoomChange(newZoom)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-3 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>PDF表示設定</DialogTitle>
        </DialogHeader>

        <div className="grid gap-2.5">
          <ReaderLayoutFields
            value={toReaderLayout(settings)}
            onChange={(layout) => onSettingsChange(applyReaderLayout(settings, layout))}
          />

          <SettingRow label="ズーム">
            <Segmented
              options={ZOOM_PRESETS}
              value={typeof settings.zoom === 'number' ? null : settings.zoom}
              onChange={handleZoomChange}
            />
            <div className="flex shrink-0 items-center">
              <Button variant="outline" size="icon" className="size-7" onClick={handleZoomOut} aria-label="縮小">
                <Minus />
              </Button>
              <span className="w-11 text-center font-mono text-xs tabular-nums">{Math.round(currentZoom * 100)}%</span>
              <Button variant="outline" size="icon" className="size-7" onClick={handleZoomIn} aria-label="拡大">
                <Plus />
              </Button>
            </div>
          </SettingRow>

          <SettingRow
            label={settings.scrollMode === ScrollMode.Horizontal ? '上下スクロールを固定' : '左右スクロールを固定'}
            htmlFor="pdf-scroll-lock"
            labelClassName="w-auto cursor-pointer"
          >
            <Switch id="pdf-scroll-lock" checked={pdfScrollLock} onCheckedChange={setPdfScrollLock} />
          </SettingRow>

          <SettingRow label="配色">
            <Segmented options={PDF_THEME_OPTIONS} value={pdfTheme} onChange={setPdfTheme} />
          </SettingRow>

          <SettingRow label="暖色" htmlFor="pdf-warmth">
            <Slider
              id="pdf-warmth"
              className="flex-1"
              value={[pdfWarmth]}
              onValueChange={([value]) => setPdfWarmth(value)}
              min={0}
              max={100}
              step={5}
            />
            <span className="w-9 shrink-0 text-right text-xs text-muted-foreground tabular-nums">{pdfWarmth}%</span>
          </SettingRow>

          <SettingRow label="フッター常時表示" htmlFor="pdf-footer-pinned" labelClassName="w-auto cursor-pointer">
            <Switch id="pdf-footer-pinned" checked={isFooterPinned} onCheckedChange={setFooterPinned} />
          </SettingRow>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={handleReset} className="w-full" style={{ borderColor: 'white' }}>
            リセット
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
